import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";
import { SortOrder } from "antd/es/table/interface";
import { Tag, Tooltip } from "antd";
import { GiftOutlined } from "@ant-design/icons";

import {
    IAppUserAuto,
    IGiveawayAutoWithDetails,
    ITaxesAuto,
} from "../../../interfaces";
import useAuth from "../../../hooks/useAuth";
import {
    formatDateStringToDate,
    formatDateStringToDateAndTime,
    poeValorComPontosEmMil,
} from "../../../utils/Conversores";
import { EmitError } from "../../../utils/EmitError";
import AppUsersAutoService from "../../../services/http/services/AppUsersAutoService";
import GiveawaysAutoService from "../../../services/http/services/GiveawaysAutoService";
import { useGetColumnSearchProps } from "../../../hooks/FilterTable/stringClearFiltros";
import { useGetColumnSearchPropsData } from "../../../hooks/FilterTable/date";
import { ThemeContext } from "../../../contexts/ThemeContext";
import TableActionButton from "../../../components/TableActionButton";
import { payment_modalities } from "../../CityTaxesAuto/useCityTaxesAuto";

function renderDateTimeCell(data: any, isDark: boolean) {
    if (!data) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const d = new Date(data);
    if (isNaN(d.getTime())) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const dateStr = d.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    const timeStr = d.toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 }}>
            <span style={{ color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {dateStr}
            </span>
            <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap', marginTop: 1 }}>
                {timeStr}
            </span>
        </div>
    );
}

export interface ITaxesTableData extends ITaxesAuto {
    key: number | undefined;
}

export interface IGiveawaysTableData extends IGiveawayAutoWithDetails {
    key: number | undefined;
    total: number;
}

interface IUseViewAppUserAutoProps {
    defaultTab?: 'impostos' | 'bilhetes';
}

export default function useViewAppUserAuto({ defaultTab }: IUseViewAppUserAutoProps = {}) {
    const { theme } = useContext<any>(ThemeContext);
    const isDark = theme === 'dark';

    const navigate = useNavigate();
    const location = useLocation();
    const { activeCity } = useAuth();
    const { id, giveawayId } = useParams();

    // Determine initial active tab
    const initialTab = useMemo(() => {
        if (defaultTab) return defaultTab;
        if (location.pathname.includes('/bilhetes')) return 'bilhetes';
        return 'impostos';
    }, [defaultTab, location.pathname]);

    const [activeTab, setActiveTab] = useState<'impostos' | 'bilhetes'>(initialTab);

    // Sync tab if URL changes
    useEffect(() => {
        if (location.pathname.includes('/bilhetes')) {
            setActiveTab('bilhetes');
        } else if (location.pathname.includes('/impostos')) {
            setActiveTab('impostos');
        }
    }, [location.pathname]);

    const [appUserAuto, setAppUserAuto] = useState<IAppUserAuto>();
    const [taxesTableData, setTaxesTableData] = useState<Array<ITaxesTableData>>([]);
    const [giveawaysTableData, setGiveawaysTableData] = useState<Array<IGiveawaysTableData>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const [taxesPagination, setTaxesPagination] = useState({ current: 1, pageSize: 50 });
    const [giveawaysPagination, setGiveawaysPagination] = useState({ current: 1, pageSize: 50 });

    // Modal state for tickets
    const [selectedGiveaway, setSelectedGiveaway] = useState<IGiveawayAutoWithDetails | null>(null);
    const [showModalTickets, setShowModalTickets] = useState<boolean>(false);

    const refFuncaoLimparFiltrosString = useRef<any>(null);

    const handleOpenModalTickets = useCallback((giveaway: IGiveawayAutoWithDetails) => {
        setSelectedGiveaway(giveaway);
        setShowModalTickets(true);
    }, []);

    const handleCloseModalTickets = useCallback(() => {
        setSelectedGiveaway(null);
        setShowModalTickets(false);
    }, []);

    const handleTabChange = useCallback((key: string) => {
        const tab = key as 'impostos' | 'bilhetes';
        setActiveTab(tab);
        if (id) {
            navigate(`/usuarios/auto/${id}/${tab}`, { replace: true });
        }
    }, [id, navigate]);

    // Taxes Filter Hooks
    const filterPaymentDate = useGetColumnSearchPropsData({ dataIndex: 'paymentDate' });
    const filterCreatedAt = useGetColumnSearchPropsData({ dataIndex: 'createdAt' });

    // Giveaways Filter Hooks
    const filterDescription = useGetColumnSearchProps({ dataIndex: 'description', ref: refFuncaoLimparFiltrosString });
    const filterPeriodBeginning = useGetColumnSearchPropsData({ dataIndex: 'period_beginning' });
    const filterPeriodEnding = useGetColumnSearchPropsData({ dataIndex: 'period_ending' });
    const filterDate = useGetColumnSearchPropsData({ dataIndex: 'date' });

    // Taxes columns (Plain array at hook top-level)
    const taxesColumns = [
        {
            title: 'Data do Pagamento',
            dataIndex: 'paymentDate',
            key: 'paymentDate',
            sorter: (a: ITaxesTableData, b: ITaxesTableData) =>
                (a.paymentDate ? new Date(a.paymentDate).getTime() : 0) - (b.paymentDate ? new Date(b.paymentDate).getTime() : 0),
            showSorterTooltip: false,
            ...filterPaymentDate,
            render: (data: any) => (
                <span style={{ color: isDark ? '#f0f0f0' : '#1e293b', fontWeight: 500 }}>
                    {data ? formatDateStringToDate(data) : '--'}
                </span>
            ),
            width: '20%',
        },
        {
            title: 'Imposto',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            sorter: {
                compare: (a: ITaxesTableData, b: ITaxesTableData) => (a.type || '').localeCompare(b.type || ''),
            },
            filters: [
                { text: 'IPTU', value: 'IPTU' },
                { text: 'ITBI', value: 'ITBI' },
            ],
            onFilter: (value: any, record: ITaxesTableData) => record.type === value,
            render: (_: any, tax: ITaxesTableData) => (
                <Tag
                    color={tax.type === 'IPTU' ? 'blue' : 'purple'}
                    style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                >
                    {tax.type}
                </Tag>
            ),
            width: '18%',
        },
        {
            title: 'Modalidade',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            align: 'center' as const,
            sorter: {
                compare: (a: ITaxesTableData, b: ITaxesTableData) => (a.paymentMethod || '').localeCompare(b.paymentMethod || ''),
            },
            filters: [
                { text: 'Cota Única', value: 'COTA_UNICA' },
                { text: 'Parcela', value: 'PARCELA' },
            ],
            onFilter: (value: any, record: ITaxesTableData) => record.paymentMethod === value,
            render: (_: any, tax: ITaxesTableData) => (
                <Tag
                    color={tax.paymentMethod === 'COTA_UNICA' ? 'green' : 'orange'}
                    style={{ borderRadius: 10, fontWeight: 500, fontSize: 11, margin: 0 }}
                >
                    {payment_modalities[tax.paymentMethod] || tax.paymentMethod}
                </Tag>
            ),
            width: '20%',
        },
        {
            title: 'Parcela',
            dataIndex: 'installmentNumber',
            key: 'installmentNumber',
            align: 'center' as const,
            sorter: (a: ITaxesTableData, b: ITaxesTableData) => (a?.installmentNumber || 0) - (b?.installmentNumber || 0),
            showSorterTooltip: false,
            render: (installmentNumber?: number) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontWeight: 500 }}>
                    {installmentNumber !== undefined && installmentNumber !== null ? installmentNumber : '--'}
                </span>
            ),
            width: '15%',
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'createdAt',
            key: 'createdAt',
            align: 'center' as const,
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITaxesTableData, b: ITaxesTableData) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            showSorterTooltip: false,
            ...filterCreatedAt,
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data, true) : '--'}
                </span>
            ),
            width: '27%',
        },
    ];

    // Giveaways columns (Plain array at hook top-level)
    const giveawaysColumns = [
        {
            title: 'Sorteio / Descrição',
            dataIndex: 'description',
            key: 'description',
            sorter: (a: IGiveawaysTableData, b: IGiveawaysTableData) => (a.description || '').localeCompare(b.description || ''),
            showSorterTooltip: false,
            width: 240,
            ...filterDescription,
            render: (desc: string) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13 }}>
                    {desc || '--'}
                </span>
            ),
        },
        {
            title: 'Início',
            dataIndex: 'period_beginning',
            key: 'period_beginning',
            align: 'center' as const,
            width: 105,
            sorter: (a: IGiveawaysTableData, b: IGiveawaysTableData) =>
                new Date(a.period_beginning).getTime() - new Date(b.period_beginning).getTime(),
            showSorterTooltip: false,
            ...filterPeriodBeginning,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Fim',
            dataIndex: 'period_ending',
            key: 'period_ending',
            align: 'center' as const,
            width: 105,
            sorter: (a: IGiveawaysTableData, b: IGiveawaysTableData) =>
                new Date(a.period_ending).getTime() - new Date(b.period_ending).getTime(),
            showSorterTooltip: false,
            ...filterPeriodEnding,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Data do Sorteio',
            dataIndex: 'date',
            key: 'date',
            align: 'center' as const,
            width: 115,
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: IGiveawaysTableData, b: IGiveawaysTableData) =>
                new Date(a.date).getTime() - new Date(b.date).getTime(),
            showSorterTooltip: false,
            ...filterDate,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            align: 'center' as const,
            width: 115,
            sorter: (a: IGiveawaysTableData, b: IGiveawaysTableData) =>
                (a.total_tickets || 0) - (b.total_tickets || 0),
            showSorterTooltip: false,
            render: (total?: number) => (
                <Tooltip color="#722ed1" title="Total de bilhetes concorrendo neste sorteio">
                    <div
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 5,
                            padding: '3px 8px',
                            borderRadius: 8,
                            background: isDark ? 'rgba(114, 46, 209, 0.12)' : '#f9f0ff',
                            border: `1px solid ${isDark ? 'rgba(114, 46, 209, 0.25)' : '#d3adf7'}`,
                            color: '#722ed1',
                            fontWeight: 600,
                            fontSize: 12.5,
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <GiftOutlined style={{ fontSize: 12 }} />
                        <span>{poeValorComPontosEmMil(total ?? 0)}</span>
                    </div>
                </Tooltip>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 80,
            fixed: 'right' as const,
            render: (_: any, record: IGiveawaysTableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar bilhetes deste sorteio"
                    onClick={() => handleOpenModalTickets(record)}
                />
            ),
        },
    ];

    // Load data
    useEffect(() => {
        if (!id) return;

        const controller = new AbortController();

        async function loadUserData() {
            try {
                setIsLoading(true);

                // 1. Fetch user data
                const appUserData = await AppUsersAutoService.getAppUserAutoById(Number(id), controller.signal);
                setAppUserAuto(appUserData);

                // Formatted taxes
                const formattedTaxes: Array<ITaxesTableData> = (appUserData?.taxesAuto || []).map((tax: ITaxesAuto) => ({
                    ...tax,
                    key: tax.id,
                }));
                setTaxesTableData(formattedTaxes);

                // 2. Fetch giveaways for this user
                const cityId = activeCity?.id || (appUserData as any)?.countyId || (appUserData as any)?.county_id;
                if (cityId) {
                    try {
                        const giveawaysList = await GiveawaysAutoService.listGiveawaysWithDetails(
                            Number(cityId),
                            Number(id),
                            controller.signal
                        );

                        const formattedGiveaways: Array<IGiveawaysTableData> = (giveawaysList || []).map(
                            (giveaway: IGiveawayAutoWithDetails) => ({
                                ...giveaway,
                                key: giveaway.id,
                                total: (giveaway.total_invoices_balance || 0) + (giveaway.total_taxes_balance || 0),
                            })
                        );
                        setGiveawaysTableData(formattedGiveaways);

                        // Check if giveawayId URL param was provided for initial modal open
                        if (giveawayId) {
                            const foundGiveaway = formattedGiveaways.find((g: IGiveawaysTableData) => String(g.id) === String(giveawayId));
                            if (foundGiveaway) {
                                setSelectedGiveaway(foundGiveaway);
                                setShowModalTickets(true);
                            }
                        }
                    } catch (giveawayErr) {
                        console.warn("Could not load giveaways for user:", giveawayErr);
                        setGiveawaysTableData([]);
                    }
                }

                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'viewAppUserAuto_loadError');
                setIsLoading(false);
            }
        }

        loadUserData();

        return () => {
            controller.abort();
        };
    }, [id, activeCity, giveawayId]);

    // Computed Stats
    const totalTicketsCount = useMemo(() => {
        if (appUserAuto?.totalTicket !== undefined && appUserAuto?.totalTicket !== null) {
            return appUserAuto.totalTicket;
        }
        return giveawaysTableData.reduce((acc, g) => acc + (g.total_tickets || 0), 0);
    }, [appUserAuto, giveawaysTableData]);

    const participatedGiveawaysCount = useMemo(() => {
        return giveawaysTableData.filter((g) => (g.total_tickets || 0) > 0).length;
    }, [giveawaysTableData]);

    return {
        id,
        appUserAuto,
        isLoading,
        activeTab,
        handleTabChange,
        taxesTableData,
        taxesColumns,
        taxesPagination,
        setTaxesPagination,
        giveawaysTableData,
        giveawaysColumns,
        giveawaysPagination,
        setGiveawaysPagination,
        selectedGiveaway,
        showModalTickets,
        handleOpenModalTickets,
        handleCloseModalTickets,
        totalTicketsCount,
        participatedGiveawaysCount,
        navigate,
    };
}
